import Plugin from '../Plugin'


export default class Join extends Plugin {

    constructor(network) {
        super(network)

        this.events = {
            'load_player': this.loadPlayer.bind(this),
            'join_room': this.joinRoom.bind(this),
            'join_igloo': this.joinIgloo.bind(this),
            'join_game_room': this.joinGameRoom.bind(this),
            'add_player': this.addPlayer.bind(this),
            'remove_player': this.removePlayer.bind(this)
        }
    }

    loadPlayer(args) {
        // Track successful login to game world
        if (window.mixpanel && args.user) {
            window.mixpanel.trackLogin(args.user)
        }

        if (this.network.saveUsername || this.network.savePassword) {
            this.savePlayer(args)
        } else {
            this.unsavePlayer(args)
        }

        // Check if WorldController already exists (reconnection scenario)
        try {
            const worldController = this.scene.get('WorldController')
            if (worldController && this.scene.isActive('WorldController')) {
                
                // Ensure the world controller has a valid room manager
                if (worldController.rooms) {
                    // Clean up any existing room to prevent conflicts
                    worldController.rooms.removeCurrent()
                }
                
                // Update existing client data without reinitializing
                this.world.setClient(args)
                
                // For reconnection, close any prompts and hide loading
                if (this.interface?.prompt) {
                    if (this.interface.prompt.error && this.interface.prompt.error.visible) {
                        this.interface.prompt.error.close()
                    }
                    if (this.interface.prompt.window && this.interface.prompt.window.visible) {
                        this.interface.prompt.window.close()
                    }
                }
                this.interface.hideLoading()
                
                // Now request to join the spawn room (don't return early!)
                // The server will send join_room which will properly load the player into a room
                return
            }
        } catch (error) {
            // Silent error handling
        }

        this.scene.start('WorldController')
        this.world.setClient(args)
        
        // Check for eligible reward packs after login (CPI-style)
        this.checkRewardPacks()
    }
    
    /**
     * Check for eligible reward packs after login
     * Shows the reward pack prompt if any unclaimed packs are available
     */
    async checkRewardPacks() {
        try {
            console.log('[Join] Checking for reward packs...')
            
            const webapi = this.network?.webapi
            if (!webapi?.isAuthenticated()) {
                console.log('[Join] WebAPI not authenticated, skipping reward pack check')
                return
            }
            
            // Small delay to ensure interface is ready
            await new Promise(resolve => setTimeout(resolve, 500))
            
            console.log('[Join] Fetching eligible reward packs from WebAPI...')
            const result = await webapi.getEligibleRewardPacks()
            console.log('[Join] Reward packs result:', result)
            
            if (result.packs && result.packs.length > 0) {
                console.log('[Join] Found eligible reward packs:', result.packs.length, result.packs)
                
                // Wait for the player to join their first room
                this.pendingRewardPacks = result.packs
                
                // Set up listener for when player enters a room
                if (!this._rewardPacksListenerSet) {
                    this._rewardPacksListenerSet = true
                    
                    // Wait a bit more then show the prompt
                    setTimeout(() => {
                        console.log('[Join] Attempting to show reward pack prompt...')
                        console.log('[Join] pendingRewardPacks:', this.pendingRewardPacks)
                        console.log('[Join] interface.prompt:', this.interface?.prompt)
                        
                        if (this.pendingRewardPacks && this.pendingRewardPacks.length > 0 && this.interface?.prompt) {
                            console.log('[Join] Showing reward packs prompt!')
                            this.interface.prompt.showRewardPacks(this.pendingRewardPacks, () => {
                                console.log('[Join] All reward packs claimed')
                                this.pendingRewardPacks = null
                            })
                        } else {
                            console.log('[Join] Could not show prompt - missing requirements')
                        }
                    }, 3000) // Show after 3 seconds to let the room load
                }
            } else {
                console.log('[Join] No eligible reward packs found')
            }
        } catch (error) {
            console.warn('[Join] Failed to check reward packs:', error)
        }
    }

    joinRoom(args) {
        // Track room join on client side
        if (window.mixpanel) {
            const roomName = this.crumbs?.rooms?.[args.room]?.name || 'Unknown Room'
            window.mixpanel.trackRoomJoin(args.room, roomName)
        }

        try {
            this.interface.showLoading(this.getString(`load_${this.crumbs.rooms[args.room].key}`), true)
            // Pass NPCs along with users if present
            this.world.joinRoom(args.room, args.users, args.npcs)
        } catch (error) {
            // Fallback: just show basic loading if string lookup fails
            if (this.interface) {
                this.interface.showLoading('Loading room...', true)
            }
            if (this.world) {
                this.world.joinRoom(args.room, args.users, args.npcs)
            }
        }
    }

    joinIgloo(args) {
        try {
            this.interface.showLoading(this.getString('load_igloo'), true)
            this.world.joinIgloo(args)
        } catch (error) {
            if (this.interface) {
                this.interface.showLoading('Loading igloo...', true)
            }
            if (this.world) {
                this.world.joinIgloo(args)
            }
        }
    }

    joinGameRoom(args) {
        try {
            this.interface.showLoading(this.getString(`load_${this.crumbs.games[args.game].key}`))
            this.world.joinGameRoom(args.game)
        } catch (error) {
            if (this.interface) {
                this.interface.showLoading('Loading game...')
            }
            if (this.world) {
                this.world.joinGameRoom(args.game)
            }
        }
    }

    addPlayer(args) {
        try {
            if (this.world) {
                this.world.addPenguin(args.user)
            }
        } catch (error) {
            // Silent error handling
        }
    }

    removePlayer(args) {
        try {
            if (this.world) {
                this.world.removePenguin(args.user)
            }
        } catch (error) {
            // Silent error handling
        }
    }

    // Saves a player to local storage
    async savePlayer(args) {
        let savedPenguins = this.network.savedPenguins

        if (Object.keys(savedPenguins).length > 6 && !(args.user.username in savedPenguins)) return

        let { photo, flag, x, y, frame, coins, id, ...penguin } = args.user

        // Handle token storage based on save preferences
        if (this.network.savePassword) {
            // Set auth token - use WebAPI refreshToken for password persistence
            let tokenToSave = null
            if (this.network.webapi && this.network.webapi.refreshToken) {
                tokenToSave = this.network.webapi.refreshToken
            } else if (this.network.token) {
                tokenToSave = this.network.token
            }
            
            // Use Network.saveToken for encryption (Finding #4 compliance)
            if (tokenToSave) {
                await this.network.saveToken(args.user.username, tokenToSave, penguin)
                return
            }
        }
        
        // No token to save - store penguin data without token
        delete penguin.token
        savedPenguins[args.user.username.toLowerCase()] = penguin
        localStorage.setItem('saved_penguins', JSON.stringify(savedPenguins))
    }

    // Deletes a player from local storage
    unsavePlayer(args) {
        let savedPenguins = this.network.savedPenguins
        let username = args.user.username.toLowerCase()

        if (username in savedPenguins) {
            // If saveUsername is enabled but not savePassword, just clear the token
            if (this.network.saveUsername && !this.network.savePassword) {
                delete savedPenguins[username].token
                localStorage.setItem('saved_penguins', JSON.stringify(savedPenguins))
            } else {
                // Otherwise, remove the penguin entirely
                delete savedPenguins[username]
                localStorage.setItem('saved_penguins', JSON.stringify(savedPenguins))
            }
        }
    }

}
